// Doctor.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { Avatar } from "@progress/kendo-react-layout";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  activeInactiveDoctor,
  deleteDoctor,
  getAllDoctors,
} from "./services/doctor.service";
import {
  clearDoctorID,
  clearFilterDoctor,
  setDoctorID,
  setFilterDoctor,
} from "./doctorSlice";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";

const Doctor: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/doctor/create");
  };

  return (
    <>
      <DeleteDoctorDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Doctor List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Doctor
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <DoctorGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const DoctorList = useAppSelector((state) => state.doctor.DoctorList);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const loading = useAppSelector((state) => state.doctor.loading);
  const FilterDoctor = useAppSelector((state) => state.doctor.FilterDoctor);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    const payload = {
      ...FilterDoctor,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllDoctors(payload));
  }, [currentPage, pageLimit]);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const MyEditCommandCell = (props: any) => {
    const handleEdit = (ID: number) => {
      navigate("/doctor/edit", { state: { DoctorID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteDoctor"));
      dispatch(setDoctorID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = async () => {
      setIsActive(!IsActive);
      const res = await dispatch(
        activeInactiveDoctor({ ID: props.dataItem.ID, IsActive: !IsActive }),
      );
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(
          getAllDoctors({
            ...FilterDoctor,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
      }
    };

    return (
      <td
        {...props.tdProps}
        style={{ textAlign: "center", ...props.tdProps.style }}
        className={props.className}
      >
        <Switch
          disabled={!isEditAccess}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  const DoctorImageCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <Avatar
        style={{ width: 50, height: 50, flexBasis: 50 }}
        rounded={"full"}
        type={"image"}
      >
        {props.dataItem?.MainImage ? (
          <img
            src={props.dataItem?.MainImage || "/placeholder-doctor.png"}
            alt={props.dataItem?.DoctorName}
            style={{
              objectFit: "cover",
              width: 50,
              height: 50,
              cursor: "pointer",
            }}
            onClick={() => window.open(props.dataItem?.MainImage, "_blank")}
          />
        ) : (
          <div
            style={{
              fontSize: "14px",
              color: "#fff",
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {"Dr."}
          </div>
        )}
      </Avatar>
    </td>
  );

  const MedicalCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <span
        style={{
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: props.dataItem?.IsMedical ? "#d4edda" : "#f8d7da",
          color: props.dataItem?.IsMedical ? "#155724" : "#721c24",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {props.dataItem?.IsMedical ? "YES" : "NO"}
      </span>
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 8 || props.index === 9 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const DoctorFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        numberfiels={["Experience", "Fees"]}
        filterSelector={(state: any) => state.doctor.FilterDoctor}
        setFilterAction={setFilterDoctor}
        clearFilterAction={clearFilterDoctor}
        fetchDataAction={getAllDoctors}
        pagination={{ case: "capitalize" }}
      />
    ),
    [],
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 200px)" }}
        filterable={true}
        filter={filter}
        className="responsive-table"
        data={DoctorList}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        total={DoctorList[0]?.ItemCount || DoctorList.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 15, 20, 25, 50],
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="MainImage"
          title="Image"
          width={"80px"}
          cells={{ data: DoctorImageCell }}
          filterable={false}
        />
        <Column
          field="DoctorName"
          title="Doctor Name"
          width={"250px"}
          filterable={true}
          cells={{
            data: (props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/doctor/edit", {
                        state: { DoctorID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.DoctorName}
                  </div>
                ) : (
                  <div className="normalName">{props.dataItem?.DoctorName}</div>
                )}
              </td>
            ),
            filterCell: DoctorFilterCell,
          }}
        />
        <Column
          field="SpecialityName"
          title="Speciality"
          width={"200px"}
          cells={{ filterCell: DoctorFilterCell }}
        />
        <Column
          field="Experience"
          title="Experience"
          width={"140px"}
          cells={{
            data: (props: any) => <td>{props.dataItem.Experience}yr</td>,
            filterCell: DoctorFilterCell,
          }}
        />
        <Column
          field="Fees"
          title="Fees (₹)"
          width={"110px"}
          cells={{ filterCell: DoctorFilterCell }}
        />
        <Column
          field="Gender"
          title="Gender"
          width={"100px"}
          cells={{ filterCell: DoctorFilterCell }}
        />
        <Column
          field="CityName"
          title="City"
          width={"120px"}
          cells={{ filterCell: DoctorFilterCell }}
        />
        <Column
          field="IsMedical"
          title="Medical Verified"
          width={"100px"}
          cells={{ data: MedicalCell }}
          filterable={false}
        />
        <Column
          field="IsActive"
          title="Status"
          width={"90px"}
          cells={{ data: MyActiveInActiveCommandCell }}
          filterable={false}
          locked={true}
        />
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="ID"
            title="Actions"
            width={"130px"}
            cells={{ data: MyEditCommandCell }}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
      {!DoctorList?.length && !loading && <AlertBox />}
    </>
  );
};

const DeleteDoctorDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const DoctorID = useAppSelector((state) => state.doctor.DoctorID);
  const loading = useAppSelector((state) => state.doctor.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterDoctor = useAppSelector((state) => state.doctor.FilterDoctor);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteDoctor(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(
          getAllDoctors({
            ...FilterDoctor,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
        dispatch(closeDialog());
        dispatch(clearDoctorID());
      } else {
        dispatch(
          getAllDoctors({
            ...FilterDoctor,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
        dispatch(closeDialog());
        dispatch(clearDoctorID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteDoctor" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Doctor</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this doctor?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(DoctorID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Doctor;
